/**
 * CrossOrgBar — the platform-admin org-switcher / acting-as banner.
 *
 * Home context + platform admin → a "Manage organization" picker.
 * While acting as another org → a persistent full-access banner + Exit.
 * Everyone else → nothing. The backend verifies platform-admin status and
 * grants full ("god mode") access in the target org; this is UI only.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus, ShieldAlert, X } from 'lucide-react';

import { useAccess, Capability } from '../../hooks/useAccess';
import { useOrganizations } from '../../hooks/useOrganizations';
import { getActingOrg, startActingAs, stopActingAs } from '../../utils/actingOrg';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const CrossOrgBar: React.FC = () => {
  const acting = getActingOrg();
  const navigate = useNavigate();
  const { can } = useAccess();
  const isPlatformAdmin = can(Capability.PlatformManage);
  // Only list orgs in the home context for a platform admin (the endpoint is
  // platform.manage-gated; while acting we show the banner instead of the picker).
  const { data: orgs, isLoading, error } = useOrganizations(!acting && isPlatformAdmin);

  // Acting as another org → persistent full-access banner (gated on the acting
  // state; the custodian has "god mode" in the target org, so make it visible).
  if (acting) {
    return (
      <div
        className="flex items-center justify-between gap-3 border-b px-4 py-2 text-sm text-foreground"
        style={{
          backgroundColor: 'rgba(217,119,6,0.14)',
          borderBottomColor: 'rgba(217,119,6,0.45)',
        }}
        role="status"
      >
        <div className="flex items-center gap-2 font-medium">
          <ShieldAlert className="size-4 shrink-0" />
          <span>
            Acting as <strong>{acting.name}</strong> as platform admin — full access
          </span>
        </div>
        <button
          type="button"
          onClick={stopActingAs}
          className="inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-xs font-semibold hover:opacity-80"
          style={{ borderColor: 'rgba(217,119,6,0.45)' }}
        >
          <X className="size-3" /> Exit
        </button>
      </div>
    );
  }

  // Home context, non-admin → nothing.
  if (!isPlatformAdmin) return null;

  const isEmpty = !!orgs && orgs.length === 0;
  const disabled = isLoading || !!error || isEmpty;
  const placeholder = isLoading
    ? 'Loading organizations…'
    : error
      ? "Couldn't load organizations"
      : isEmpty
        ? 'No organizations'
        : 'Select an organization…';

  return (
    <div className="flex items-center gap-2 border-b border-border bg-background px-4 py-1.5 text-sm">
      <Building2 className="size-4 text-muted-foreground" />
      <span className="text-muted-foreground">Manage organization</span>
      <Select
        disabled={disabled}
        onValueChange={(orgId) => {
          const org = orgs?.find((o) => o.organizationId === orgId);
          if (org) startActingAs({ id: org.organizationId, name: org.name });
        }}
      >
        <SelectTrigger className="h-7 w-64 text-xs" aria-label="Select an organization to view">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {orgs?.map((org) => (
            <SelectItem key={org.organizationId} value={org.organizationId}>
              {org.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <button
        type="button"
        onClick={() => navigate('/v4/admin/organizations')}
        className="ml-auto inline-flex items-center gap-1 rounded-sm border border-border px-2 py-1 text-xs font-semibold text-foreground hover:bg-muted"
      >
        <Plus className="size-3.5" /> New organization
      </button>
    </div>
  );
};

export default CrossOrgBar;
